import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Eye, Loader2, Send } from "lucide-react";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { errMsg } from "./ResolveDialog";

const KINDS = [
  ["invitation", "Invitation"], ["reminder", "Reminder"],
  ["summary_ready", "Summary ready"], ["opt_out", "Stop-emails confirmation"],
];
const USES_TEMPLATE = ["invitation", "reminder"];
const SELECT = "w-full h-9 rounded-md border border-zinc-300 bg-white px-2 text-sm";

function useVersions() {
  const [rows, setRows] = useState([]);
  useEffect(() => {
    api.get("/checkin-templates").then((r) => setRows(r.data.flatMap((t) => t.versions.filter((v) => v.status !== "retired").map((v) => ({
      id: v.id, label: `${t.name} — v${v.semantic_version} (${v.status.replace("_", " ")})`, status: v.status,
    }))))).catch(() => setRows([]));
  }, []);
  return rows;
}

function Controls({ f, set, versions, canSend, busy, onPreview, onSend }) {
  return (
    <div className="space-y-3">
      <div><Label>Email</Label>
        <select className={SELECT} value={f.kind} onChange={(e) => set({ kind: e.target.value })} data-testid="email-preview-kind">
          {KINDS.map(([k, l]) => <option key={k} value={k}>{l}</option>)}
        </select>
      </div>
      {USES_TEMPLATE.includes(f.kind) && (
        <div><Label>Template version</Label>
          <select className={SELECT} value={f.version_id} onChange={(e) => set({ version_id: e.target.value })} data-testid="email-preview-version">
            {versions.map((v) => <option key={v.id} value={v.id}>{v.label}</option>)}
          </select>
        </div>
      )}
      {USES_TEMPLATE.includes(f.kind) && (
        <div><Label>Email wording</Label>
          <select className={SELECT} value={f.wording} onChange={(e) => set({ wording: e.target.value })} data-testid="email-preview-wording">
            <option value="live">Live wording</option>
            <option value="draft">Draft wording (if saved)</option>
          </select>
        </div>
      )}
      <div><Label>Sample first name</Label>
        <Input value={f.preferred_name} maxLength={60} onChange={(e) => set({ preferred_name: e.target.value })} data-testid="email-preview-name" />
      </div>
      <Button variant="outline" size="sm" onClick={onPreview} disabled={!!busy} data-testid="email-preview-btn">
        {busy === "preview" ? <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" /> : <Eye className="mr-1.5 h-3.5 w-3.5" />} Preview
      </Button>
      {canSend && (
        <div className="space-y-1.5 border-t border-zinc-200 pt-3">
          <Label>Send a test to</Label>
          <Input type="email" value={f.to} onChange={(e) => set({ to: e.target.value })} placeholder="name@example.com" data-testid="email-test-to" />
          <Button size="sm" onClick={onSend} disabled={!!busy || !f.to.trim()} data-testid="email-test-send-btn">
            {busy === "send" ? <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" /> : <Send className="mr-1.5 h-3.5 w-3.5" />} Send test
          </Button>
          <p className="text-2xs text-zinc-500">Marked [TEST] with a sample name; links open a harmless demo page.</p>
        </div>
      )}
    </div>
  );
}

export function EmailPreviewCard() {
  const { user } = useAuth();
  const versions = useVersions();
  const [f, setF] = useState({ kind: "invitation", version_id: "", wording: "live", preferred_name: "Alex", to: user?.email || "" });
  const [preview, setPreview] = useState(null);
  const [busy, setBusy] = useState(null);
  const set = (patch) => { setF((x) => ({ ...x, ...patch })); setPreview(null); };
  useEffect(() => { if (user?.email) setF((x) => (x.to ? x : { ...x, to: user.email })); }, [user?.email]);
  useEffect(() => {
    if (!f.version_id && versions.length) {
      setF((x) => ({ ...x, version_id: (versions.find((v) => v.status === "published") || versions[0]).id }));
    }
  }, [versions, f.version_id]);
  const payload = () => ({ kind: f.kind, version_id: f.version_id || undefined, wording: f.wording, preferred_name: f.preferred_name.trim() || "Alex" });
  async function doPreview() {
    setBusy("preview");
    try { setPreview((await api.post("/checkin-email-preview", payload())).data); }
    catch (e) { toast.error("Preview failed", { description: errMsg(e) }); }
    finally { setBusy(null); }
  }
  async function doSend() {
    setBusy("send");
    try {
      const r = await api.post("/checkin-email-test-send", { ...payload(), to: f.to.trim() });
      toast.success("Test email sent", { description: `${r.data.subject} → ${r.data.to}` });
    } catch (e) { toast.error("Test send failed", { description: errMsg(e) }); }
    finally { setBusy(null); }
  }
  return (
    <Card className="p-4" data-testid="email-preview-card">
      <h3 className="font-display font-semibold">Email preview & test send</h3>
      <p className="mb-3 text-xs text-zinc-500">Check client emails before they go out. Previews never contact a client.</p>
      <div className="grid gap-4 md:grid-cols-[280px_1fr]">
        <Controls f={f} set={set} versions={versions} canSend={user?.role !== "auditor"} busy={busy} onPreview={doPreview} onSend={doSend} />
        <div className="min-h-[420px] overflow-hidden rounded-md border border-zinc-200 bg-zinc-50">
          {preview ? (
            <>
              <div className="border-b border-zinc-200 bg-white px-3 py-2 text-sm" data-testid="email-preview-subject">
                <span className="text-zinc-500">Subject:</span> <b>{preview.subject}</b>
              </div>
              <iframe title="Email preview" srcDoc={preview.html} sandbox="" className="h-[460px] w-full bg-white" data-testid="email-preview-frame" />
            </>
          ) : (
            <div className="flex h-full min-h-[420px] items-center justify-center p-6 text-center text-sm text-zinc-500" data-testid="email-preview-empty">
              Choose an email and press Preview.
            </div>
          )}
        </div>
      </div>
    </Card>
  );
}
